import { EnvironmentId } from 'api/apiTypes';
import { MiddlewareError, MiddlewareEvent } from 'api/events';
import { useMiddlewareHookInit, useMiddlewareHookUpdate } from 'api/events/hooks';
import routes from 'api/routes';
import { error } from 'api/utils';
import { useCallback } from 'react';
import useSWR from 'swr';

import { useAxios } from 'utils/transport/useAxios';

import { ApiPrivacyResponseSuccessSchema } from './types/api';
import ApiPrivacyConsentConfiguration, { ApiPrivacyConsentConfigurationSchema } from './types/api/consentConfiguration';

export function usePrivacyConsent(envGuid: EnvironmentId | null, dbGuid: string | null) {
  const { get } = useAxios();

  const initEvent = useMiddlewareHookInit('Middleware', 'usePrivacyConsent()');
  const updateEvent = useMiddlewareHookUpdate('Middleware', 'usePrivacyConsent()', initEvent?.id);

  return useSWR(
    envGuid && dbGuid ? routes.getPrivacyConsent(envGuid, dbGuid) : null,
    async (url: string) => {
      const response = await get(url, { withCredentials: true });
      const validateEvent = new MiddlewareEvent({
        name: 'Validating privacy consent configuration',
        namespace: 'Middleware',
        path: 'usePrivacyConsent()',
        parent: updateEvent.id,
        source: 'validate',
      });

      const data = ApiPrivacyResponseSuccessSchema.parse(response.data);

      validateEvent.data = data.data;

      try {
        const parsedData = ApiPrivacyConsentConfigurationSchema.parse(data.data);
        validateEvent.status = 'completed';
        return parsedData;
      } catch (e) {
        validateEvent.status = 'failed';
        if (e instanceof Error) {
          throw new MiddlewareError({
            cause: e,
            event: validateEvent,
          });
        } else {
          throw e;
        }
      }
    },
    {
      errorRetryCount: 3,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
}

export function useSavePrivacyConsent(envGuid: EnvironmentId | null, dbGuid: string | null) {
  const { put } = useAxios();

  const savePrivacyConsent = useCallback(
    (data: ApiPrivacyConsentConfiguration) => {
      if (!dbGuid) return error(['DB GUID not found']);
      if (!envGuid) return error(['Environment GUID not found']);
      const z = ApiPrivacyConsentConfigurationSchema.parse(data);

      return put(routes.savePrivacyConsent(envGuid, dbGuid), z, { withCredentials: true });
    },
    [dbGuid, envGuid, put]
  );

  return savePrivacyConsent;
}
